import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button, Card, CardBody, Chip, Spinner } from "@heroui/react";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { LimePageHeader } from "@/Components/LimePageHeader";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { useClientFeature } from "@/hooks/useClientFeature";
import type { GetCalendar } from "@/server-types";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { useCalendarStore } from "../store/useCalendarStore";
import { CalendarModeToggle } from "../components/CalendarModeToggle";
import { formatListDayHeader } from "./formatDayHeader";

type CalendarAppointment =
  GetCalendar["response"]["calendarData"]["allAppointments"][number];
type AppointmentCustomer = CalendarAppointment["customers"][number];

/**
 * Mobile-first list rendering of the day's appointments — the calendar's
 * "list" mode, and what the native app opens on. It is a read-only
 * cashier view: rows show the invoice status per customer and tap through
 * to the existing tax-register flow (issued → invoice details, not issued →
 * POS pre-filled via ?uaid/?auid — same handoff CalendarModalWrapper uses).
 * Data comes from the same `useCalendarStore` / `GET /calendar/2` pipeline
 * as the grid, so saved filters and the 5-minute refresh apply unchanged.
 */
export const CalendarListView = () => {
  const navigate = useNavigate();
  const { isFeatureEnabled } = useClientFeature();
  const permissions = useUserPermissionsStore((state) => state.permissions);

  const {
    selectedDate,
    dateActions,
    locationActions,
    refreshCalendar,
    fetchingCalendarData,
    calendarData,
    locations,
    selectedLocationId,
    users,
    showOnlyFixedAppointments,
  } = useCalendarStore((state) => state);

  useEffect(() => {
    // The list is always a single-day view; setCalendarView refreshes when
    // the view actually changes, the explicit refresh covers first mount.
    dateActions.setCalendarView("day");
    refreshCalendar();

    const interval = setInterval(
      () => {
        refreshCalendar(true);
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateKey = selectedDate.format("YYYY-MM-DD");

  const appointments = useMemo(() => {
    const allAppointments: CalendarAppointment[] =
      calendarData?.allAppointments ?? [];

    return allAppointments
      .filter(
        (appointment) =>
          appointment.date === dateKey &&
          appointment.filterEnabled !== false &&
          !appointment.isDeleted &&
          (!showOnlyFixedAppointments ||
            appointment.fixedAppointmentId != null),
      )
      .sort((a, b) => (a.startTime ?? "").localeCompare(b.startTime ?? ""));
  }, [calendarData, dateKey, showOnlyFixedAppointments]);

  const isTaxRegisterEnabled = isFeatureEnabled("taxRegister");

  const canOpenInvoice = (customer: AppointmentCustomer): boolean => {
    if (!isTaxRegisterEnabled) return false;
    return customer.invoiceId
      ? !!permissions.view_invoice_archive_page
      : !!permissions.view_pos_page;
  };

  /**
   * Same handoff as the appointment drawer's receipt button: issued →
   * invoice details in the archive, not issued → POS pre-filled with the
   * customer's user appointment.
   */
  const openInvoiceFlow = (
    appointment: CalendarAppointment,
    customer: AppointmentCustomer,
  ) => {
    if (!canOpenInvoice(customer)) return;

    if (customer.invoiceId) {
      navigate(`/dashboard/tax-register/invoice/${customer.invoiceId}`);
      return;
    }

    const employeeUserId = appointment.userIds?.[0];
    navigate(
      `/dashboard/tax-register/invoice/new?uaid=${customer.userAppointmentId}${
        employeeUserId ? `&auid=${employeeUserId}` : ""
      }`,
    );
  };

  const employeeNames = (appointment: CalendarAppointment): string => {
    const names = (appointment.userIds ?? [])
      .map(
        (userId: number) =>
          users?.find(
            (user: { userId: number; name?: string }) => user.userId === userId,
          )?.name,
      )
      .filter(Boolean);
    return names.join(", ");
  };

  const customerLabel = (customer: AppointmentCustomer): string =>
    `${customer.name ?? ""} ${customer.lastName ?? ""}`.trim();

  const invoiceChip = (customer: AppointmentCustomer) =>
    isTaxRegisterEnabled ? (
      <Chip
        size="sm"
        variant="flat"
        color={customer.invoiceId ? "success" : "warning"}
      >
        {customer.invoiceId ? (
          <Trans>Račun izdan</Trans>
        ) : (
          <Trans>Ni zaračunano</Trans>
        )}
      </Chip>
    ) : null;

  return (
    <div className="flex h-[calc(100dvh_-_var(--safe-area-top,0px))] flex-col">
      <LimePageHeader title={t`Koledar`} />

      <div className="flex flex-col gap-2 px-4 pt-2 pb-2">
        <div className="flex items-center justify-between gap-2">
          <Button
            isIconOnly
            variant="flat"
            aria-label={t`Prejšnji dan`}
            onPress={() =>
              dateActions.setSelectedDate(selectedDate.subtract(1, "day"))
            }
          >
            <ChevronLeft size={18} />
          </Button>

          <span className="font-semibold capitalize">
            {formatListDayHeader(selectedDate, i18n.locale)}
          </span>

          <Button
            isIconOnly
            variant="flat"
            aria-label={t`Naslednji dan`}
            onPress={() =>
              dateActions.setSelectedDate(selectedDate.add(1, "day"))
            }
          >
            <ChevronRight size={18} />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <CalendarModeToggle />

          <Button
            size="sm"
            variant="flat"
            className="shrink-0"
            onPress={() => dateActions.setSelectedDate(dayjs())}
          >
            <Trans>Danes</Trans>
          </Button>

          {locations != null && locations.length > 1 ? (
            <LimeSelect
              aria-label={t`Lokacija`}
              size="sm"
              items={locations.map(
                (location: { locationId: number; name: string }) => ({
                  key: location.locationId.toString(),
                  label: location.name,
                }),
              )}
              value={selectedLocationId?.toString()}
              onSingleItemChange={(value: string) => {
                locationActions.setSelectedLocationId(Number(value));
                refreshCalendar();
              }}
              disallowEmptySelection
            />
          ) : null}
        </div>
      </div>

      <div className="flex grow flex-col gap-2 overflow-y-auto px-4 pt-2 pb-[calc(1rem_+_var(--safe-area-bottom,0px))]">
        {fetchingCalendarData && appointments.length === 0 ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : appointments.length === 0 ? (
          <p className="text-foreground-500 py-10 text-center text-sm">
            <Trans>Za izbrani dan ni terminov.</Trans>
          </p>
        ) : (
          appointments.map((appointment) => {
            const visibleCustomers = (appointment.customers ?? []).filter(
              (customer: AppointmentCustomer) => customer.show !== false,
            );
            const employees = employeeNames(appointment);
            const singleCustomer =
              visibleCustomers.length === 1 ? visibleCustomers[0] : null;

            return (
              <Card
                key={appointment.appointmentId}
                isPressable={
                  singleCustomer != null && canOpenInvoice(singleCustomer)
                }
                shadow="none"
                className="border-default-200 w-full shrink-0 border"
                onPress={() => {
                  if (singleCustomer) {
                    openInvoiceFlow(appointment, singleCustomer);
                  }
                }}
              >
                <CardBody className="flex flex-col gap-2 p-3">
                  <div className="flex flex-row items-stretch gap-3">
                    <div className="flex w-12 shrink-0 flex-col items-center justify-center">
                      <span className="text-sm font-semibold">
                        {appointment.formattedData?.startTime ??
                          appointment.startTime}
                      </span>
                      <span className="text-foreground-400 text-xs">
                        {appointment.formattedData?.endTime}
                      </span>
                    </div>

                    <div
                      className="w-1 shrink-0 rounded-full"
                      style={{
                        backgroundColor: appointment.serviceColor || "#74B587",
                      }}
                    />

                    <div className="flex min-w-0 grow flex-col justify-center gap-0.5">
                      <span className="truncate text-start text-sm font-medium">
                        {appointment.serviceName}
                      </span>

                      {singleCustomer ? (
                        <span className="text-foreground-600 truncate text-start text-sm">
                          {customerLabel(singleCustomer)}
                        </span>
                      ) : null}

                      {employees ? (
                        <span className="text-foreground-400 truncate text-start text-xs">
                          {employees}
                        </span>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 flex-col items-end justify-center gap-1">
                      {singleCustomer ? invoiceChip(singleCustomer) : null}
                      {(appointment.numberOfPeople ?? 0) > 1 ? (
                        <Chip size="sm" variant="flat">
                          {visibleCustomers.length}/{appointment.numberOfPeople}
                        </Chip>
                      ) : null}
                    </div>
                  </div>

                  {/* Group appointments: each customer is charged
                      separately, so each gets their own row + status. */}
                  {visibleCustomers.length > 1 ? (
                    <div className="border-default-100 flex flex-col gap-1 border-t pt-2">
                      {visibleCustomers.map((customer: AppointmentCustomer) => (
                        <button
                          key={customer.userAppointmentId}
                          type="button"
                          disabled={!canOpenInvoice(customer)}
                          className="flex items-center justify-between gap-2 rounded-lg px-1 py-1 text-start"
                          onClick={() => openInvoiceFlow(appointment, customer)}
                        >
                          <span className="text-foreground-600 truncate text-sm">
                            {customerLabel(customer)}
                          </span>
                          {invoiceChip(customer)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </CardBody>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};
